import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { DeletedFilled } from 'assets/images/svg';
import fieldsValidator from 'library/helpers/validators/fieldsValidator';
import { useManagePermittedRoles } from 'library/hooks';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAssetGroups } from 'reduxStore/assetGroups/selectors';
import { assetGroupActions } from 'reduxStore/assetGroups/slice';
import { selectDevices } from 'reduxStore/devices/selectors';
import { selectSites } from 'reduxStore/sites/selectors';
import { selectDeviceGroupsTypes } from 'reduxStore/types/selectors';
import { WarningAlert } from 'zComponents/atoms';
import {
	Button,
	Col,
	Divider,
	Empty,
	Flex,
	Form,
	Input,
	List,
	Modal,
	Radio,
	Row,
	Select,
	Space,
	Tooltip,
	Typography
} from 'zui/Antd';

const { Title, Text } = Typography;
const { TextArea } = Input;

const AddAssetGroupModal = ({ children, visible: visibleProp, setVisible: setVisibleProp }) => {
	// ✅ Hooks
	const dispatch = useDispatch();
	const canCreateGroup = useManagePermittedRoles('createGroup');
	const isGlobalAdmin = useManagePermittedRoles('manageSiteAssignment');
	const permissionDeniedMessage = "You don't have permission to perform this action.";
	const {
		token: {
			fontSizeSM,
			paddingXL,
			paddingLG,
			colorTextDescription,
			fontWeightStrong,
			colorIcon,
			paddingXS,
			colorBgElevated,
			colorText,
			paddingSM,
			colorBorder,
			borderMD = 6,
			paddingXXS,
			colorTextLabel
		}
	} = theme.useToken();
	const [form] = Form.useForm();

	// 🛡️ Multi-tenancy: Get permitted sites
	const permittedSites = useSelector(selectSites);

	// ✅ Redux States
	const devicesData = useSelector(selectDevices);
	const groupsTypes = useSelector(selectDeviceGroupsTypes);
	const deviceGroupsData = useSelector(selectAssetGroups);

	const { completeAddGroup, loadingAddGroup } = useSelector((state) => state.AssetGroups);

	// ✅ Local States
	const [selectedDevices, setSelectedDevices] = useState([]);
	const [internalVisible, setInternalVisible] = useState(false);

	// ✅ Handlers
	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	// 🎯 Native Form Initialization for Site Selection
	useEffect(() => {
		if (visible && !isGlobalAdmin) {
			const initialSite = permittedSites?.length > 0 ? permittedSites[0].siteID : null;
			if (initialSite) {
				form.setFieldsValue({ siteID: initialSite });
			}
		}
	}, [visible, permittedSites, form, isGlobalAdmin]);

	// ✅ Effect to handle the completion of adding a grouprend
	useEffect(() => {
		if (completeAddGroup) {
			setTimeout(() => {
				setVisible(false);
				form.resetFields();
				setSelectedDevices([]);
			}, 500); // Small delay to ensure UI updates properly
		}
	}, [completeAddGroup, form, setVisible]);

	// ✅ Handlers
	const handleCancel = () => {
		setVisible(false);
		form.resetFields();
	};

	// ✅ Submit the form
	const handleSubmit = (values) => {
		dispatch(
			assetGroupActions.addGroup({
				data: { ...values, assetIDs: selectedDevices.map((device) => device.deviceID) }
			})
		);
	};

	// ✅ Select Device
	const handleSelectDevice = (deviceUID) => {
		const selectedDevice = devicesData?.find((d) => d.deviceID === deviceUID);
		if (selectedDevice && !selectedDevices.some((d) => d.deviceID === deviceUID)) {
			setSelectedDevices([...selectedDevices, selectedDevice]);
		}
	};

	// ✅ Remove Device
	const handleRemoveDevice = (deviceID) => {
		setSelectedDevices((prev) => prev.filter((d) => d.deviceID !== deviceID));
	};

	// 📍 Watch Selected Site to filter available device options
	const selectedSiteID = Form.useWatch('siteID', form);

	// ✅ Device Options (Filtered by selected site)
	const availableDevicesOptions = useMemo(() => {
		return devicesData
			.filter(
				(device) =>
					device.sites?.some((s) => s.siteID === selectedSiteID) &&
					!selectedDevices.some((d) => d.deviceID === device.deviceID)
			)
			.map((device) => ({
				label: device.name,
				value: device.deviceID
			}));
	}, [devicesData, selectedDevices, selectedSiteID]);

	const [showAlert, setShowAlert] = useState(false);
	const [showDeviceGroupAlert, setShowDeviceGroupAlert] = useState(false);
	const nameValue = Form.useWatch('name', form);

	// 📍 Modified Validation: Check duplicate names only within the same site
	useEffect(() => {
		if (nameValue && selectedSiteID) {
			const isDuplicate = deviceGroupsData.some(
				(group) => group.siteID === selectedSiteID && group.name.toLowerCase() === nameValue.toLowerCase()
			);
			setShowAlert(isDuplicate);
		} else {
			setShowAlert(false);
		}
	}, [deviceGroupsData, nameValue, selectedSiteID]);

	const selectedIDs = useMemo(() => {
		return selectedDevices.map((d) => d.deviceID).sort((a, b) => a - b);
	}, [selectedDevices]);

	// 📍 Modified Validation: Check exact duplicate groups only within the same site
	useEffect(() => {
		if (selectedIDs.length === 0 || !selectedSiteID) {
			setShowDeviceGroupAlert(false);
			return;
		}

		const duplicate = deviceGroupsData.some((group) => {
			if (group.siteID !== selectedSiteID) return false;
			if (!Array.isArray(group.devices)) return false;

			const groupIDs = [...group.devices].sort((a, b) => a - b);

			return groupIDs.length === selectedIDs.length && groupIDs.every((id, idx) => id === selectedIDs[idx]);
		});

		setShowDeviceGroupAlert(duplicate);
	}, [deviceGroupsData, selectedIDs, selectedSiteID]);

	return (
		<>
			<Tooltip title={!canCreateGroup ? permissionDeniedMessage : 'Create Device Group'}>
				<span>
					{children ? (
						children
					) : (
						<Button
							type='primary'
							disabled={!canCreateGroup}
							onClick={() => canCreateGroup && setVisible(true)}
						>
							Create Device Group
						</Button>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				destroyOnClose
				width={1154}
				closeIcon={<CloseOutlined />}
				centered
				footer={[
					<Button key='cancel' onClick={handleCancel}>
						Cancel
					</Button>,
					<Button
						htmlType='submit'
						type='primary'
						key='submit'
						form='addAssetGroupForm'
						loading={loadingAddGroup}
						disabled={loadingAddGroup || showAlert || showDeviceGroupAlert}
					>
						Create Group
					</Button>
				]}
				styles={{
					body: {
						height: 617,
						display: 'flex',
						flexDirection: 'column'
					},
					content: { padding: paddingXL, height: '100%' },
					footer: { marginTop: 0 }
				}}
			>
				<Row style={{ height: '100%' }}>
					{/* Left Panel */}
					<Col span={11} style={{ height: '100%' }}>
						<Flex vertical style={{ marginBottom: paddingXL }}>
							<Title level={4} style={{ margin: 0 }}>
								Create Device Group
							</Title>
							<Text
								style={{
									fontSize: fontSizeSM,
									color: colorTextDescription,
									lineHeight: '20px'
								}}
							>
								A device group is a customisable group of devices designed to streamline permission
								management. Assigning a person permission to a device group would give them access to
								all devices in the group.
							</Text>
						</Flex>
						<Form id='addAssetGroupForm' onFinish={handleSubmit} form={form} layout='vertical' requiredMark={false}>
							{/* ✅ Group Name & asset */}
							<Row style={{ marginBottom: 0 }} gutter={[12, paddingXL]} align={'middle'}>
								<Col sm={24} xs={24} lg={24}>
									<Form.Item
										name='name'
										label='Device Group Name'
										rules={fieldsValidator.Name}
										style={{ fontWeight: fontWeightStrong, marginBottom: 0 }}
									>
										<Input placeholder='e.g. Communal Room' />
									</Form.Item>
									{showAlert && (
										<Flex style={{ paddingTop: paddingXS, width: '100%' }} justify='center'>
											<WarningAlert
												marginBottom={0}
												message='A Device Group with this name already exists in the selected site.'
											/>
										</Flex>
									)}
								</Col>
								{/* 📍 MULTI-TENANT SITE SELECTOR */}
								<Col sm={24} xs={24} lg={24}>
									<Form.Item
										name='siteID'
										label='Select Site'
										rules={[
											{ required: !isGlobalAdmin, message: 'Please select a site to create this device group.' }
										]}
										style={{ fontWeight: fontWeightStrong, marginBottom: 0 }}
										extra={
											!isGlobalAdmin && permittedSites?.length <= 1 && (
												<Text type='secondary' style={{ fontSize: 12, fontWeight: 'normal' }}>
													This device group will automatically be created in your assigned site.
												</Text>
											)
										}
									>
										<Select
											placeholder='Select a site'
											allowClear={isGlobalAdmin}
											disabled={!isGlobalAdmin && permittedSites?.length <= 1}
											options={permittedSites?.map((s) => ({
												label: s.name,
												value: s.siteID
											}))}
											onChange={() => {
												// Clear selected devices if site changes,
												// because devices belong to specific sites.
												setSelectedDevices([]);
											}}
										/>
									</Form.Item>
								</Col>

								<Col sm={24} xs={24} lg={24}>
									<Form.Item
										name='description'
										label='Device Group Description'
										rules={fieldsValidator.Description}
										style={{ fontWeight: fontWeightStrong, marginBottom: 0 }}
									>
										<TextArea
											style={{ color: colorTextDescription }}
											placeholder='This device group is...'
											allowClear
											autoSize={{ minRows: 3, maxRows: 5 }}
										/>
									</Form.Item>
								</Col>
								<Col sm={24} xs={24} lg={24}>
									<Form.Item
										name='typeID'
										label='Type'
										tooltip={{
											color: colorBgElevated,
											title: (
												<Text style={{ color: colorText, fontWeight: 500 }}>
													Defines the device's function within the system.
												</Text>
											),
											icon: (
												<QuestionCircleOutlined
													style={{
														fontSize: fontSizeSM,
														marginLeft: '8px',
														color: colorIcon
													}}
												/>
											)
										}}
										style={{ fontWeight: fontWeightStrong }}
										rules={[{ required: true, message: 'Please select a type' }]}
									>
										<Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
											{groupsTypes.map((asset) => (
												<Radio
													key={asset.groupTypeID}
													value={asset.groupTypeID}
													style={{
														color: colorText,
														fontWeight: '500',
														lineHeight: '22px'
													}}
												>
													{asset.name}
													<Tooltip
														color={colorBgElevated}
														style={{ fontSize: fontSizeSM }}
														title={
															<span style={{ color: colorText, fontWeight: 500 }}>
																{asset.name === 'General'
																	? 'Use for standard devices that are not involved in access control, like sensors or readers.'
																	: asset.name === 'Access Control'
																		? 'Use for devices that control or monitor entry points, such as doors, gates, or elevators.'
																		: ''}
															</span>
														}
													>
														<QuestionCircleOutlined
															style={{
																marginLeft: paddingXS,
																color: colorIcon,
																fontSize: fontSizeSM
															}}
														/>
													</Tooltip>
												</Radio>
											))}
										</Radio.Group>
									</Form.Item>
								</Col>
							</Row>
						</Form>
					</Col>

					{/* Divider */}
					<Col span={2} style={{ display: 'flex', justifyContent: 'center', height: '100%' }}>
						<Divider type='vertical' style={{ height: '100%', margin: `0px ${paddingXS}px` }} />
					</Col>

					{/* Right Panel */}
					<Col span={11} style={{ height: '100%' }}>
						{/* ✅ Device Connect Section */}
						<Space direction='vertical' size={paddingXS} style={{ width: '100%', marginBottom: paddingLG }}>
							<Text style={{ fontWeight: fontWeightStrong }}>Add Devices to Device Group (Optional)</Text>
							<Select
								showSearch
								placeholder={!selectedSiteID ? 'Select a site first' : 'Enter Device Name(s)'}
								style={{ width: '100%', marginBottom: 0 }}
								options={availableDevicesOptions}
								value={null}
								disabled={!selectedSiteID}
								onSelect={(value) => {
									handleSelectDevice(value);
								}}
								filterOption={(input, option) =>
									option?.label?.toLowerCase().includes(input.toLowerCase())
								}
							/>
						</Space>
						{/* ✅ Device List */}
						<Flex gap={paddingXS} vertical>
							<Flex align='center' justify='space-between'>
								<Text strong>Selected Devices</Text>
								<Button
									type='text'
									onClick={() => setSelectedDevices([])}
									style={{ textDecoration: 'underline' }}
								>
									Clear List
								</Button>
							</Flex>

							<Flex
								style={{
									height: 378,
									overflowY: 'auto',
									padding: `${paddingSM}px 0px`,
									borderRadius: borderMD,
									border: `1px solid ${colorBorder}`,
									width: 'calc(100% - 2px)'
								}}
							>
								{selectedDevices && selectedDevices.length > 0 ? (
									<List
										dataSource={selectedDevices}
										split={false}
										style={{ width: '100%' }}
										renderItem={(device, index) => (
											<List.Item
												key={device.deviceID}
												style={{
													padding: `${paddingXXS}px 20px`,
													width: '100%',
													borderBottom: 'none',
													marginBottom: index < selectedDevices.length - 1 ? paddingXXS : 0
												}}
												extra={
													<Button
														icon={<DeletedFilled width={16} height={16} />}
														onClick={() => handleRemoveDevice(device.deviceID)}
														type='text'
														style={{ color: colorTextLabel }}
													/>
												}
											>
												<List.Item.Meta
													title={<Text style={{ fontWeight: 400 }}>{device.name}</Text>}
												/>
											</List.Item>
										)}
									/>
								) : (
									<Flex
										align='center'
										justify='center'
										style={{
											height: '100%',
											width: '100%'
										}}
									>
										<Empty
											description={
												!selectedSiteID ? 'Select a site first' : 'Add devices to this group'
											}
											image={Empty.PRESENTED_IMAGE_SIMPLE}
										/>
									</Flex>
								)}
							</Flex>
						</Flex>
						{showDeviceGroupAlert && (
							<Flex style={{ padding: `${paddingSM}px 0px`, width: '100%' }} justify='center'>
								<WarningAlert message='A Device Group with these devices already exists.' />
							</Flex>
						)}
					</Col>
				</Row>
			</Modal>
		</>
	);
};

export default AddAssetGroupModal;
